// ---------------------------------------------------------------------------
// DnD Email Editor – Typography styling (single source of truth)
//
// Rich-text content is stored as raw <h1>/<h2>/<h3>/<p>/<a> markup with no
// inline styles. Both the HTML compiler (dnd-compiler) and the canvas preview
// (DndCanvas) run that content through `inlineContentStyles()` so heading /
// paragraph / link styling is applied identically in the editor and the export
// — and, critically, so it SURVIVES in email clients (Gmail, Yahoo,
// Outlook.com) that strip the <head><style> block, and is not flattened by the
// app's Tailwind Preflight reset in the canvas.
//
// `typographyCss()` emits the equivalent rules for the <style> block as
// progressive enhancement. Mirrors the shared-module pattern in dnd-columns.ts.
// ---------------------------------------------------------------------------

import type { GlobalStyles, HeadingStyle } from './dnd-model';

// Fallbacks so a user who clears a color never produces an invalid
// `color: ;` declaration (which cascades to browser-default black).
const FALLBACK_HEADING_COLOR = '#222222';
const FALLBACK_TEXT_COLOR = '#333333';
const FALLBACK_LINK_COLOR = '#1a73e8';

/** Link `text-decoration` value for the current global setting. */
function linkDecoration(gs: GlobalStyles): string {
  return gs.linkUnderline ? 'underline' : 'none';
}

/** Inline-style map applied to a heading tag of the given level. */
function headingStyleMap(gs: GlobalStyles, h: HeadingStyle): Record<string, string> {
  return {
    'font-family': gs.headingFontFamily,
    'font-size': h.fontSize,
    color: h.color || FALLBACK_HEADING_COLOR,
    'font-weight': String(h.fontWeight),
    margin: '0 0 10px',
  };
}

/** Inline-style map applied to <a> tags. */
function linkStyleMap(gs: GlobalStyles): Record<string, string> {
  return {
    color: gs.linkColor || FALLBACK_LINK_COLOR,
    'text-decoration': linkDecoration(gs),
  };
}

/**
 * Inline-style map applied to <ul> / <ol>.
 *
 * `list-style-type` is set explicitly for two reasons: Tailwind Preflight resets
 * `ol, ul { list-style: none }` app-wide, which would leave the canvas with no
 * markers, and some webmail clients apply a similar reset. Indentation uses
 * `margin-left` rather than `padding-left` because Outlook's Word engine ignores
 * list padding.
 */
function listStyleMap(type: 'disc' | 'decimal'): Record<string, string> {
  return {
    'list-style-type': type,
    margin: '0 0 10px 20px',
    padding: '0',
  };
}

/** Inline-style map applied to <li>. */
const LIST_ITEM_STYLE: Record<string, string> = { margin: '0 0 4px' };

/**
 * CSS rules for the <head><style> block (progressive enhancement for clients
 * that keep <style>; the same styles are inlined onto elements by
 * `inlineContentStyles` so rendering does not depend on <style> surviving).
 */
export function typographyCss(gs: GlobalStyles): string {
  const h = (hs: HeadingStyle) =>
    `font-family: ${gs.headingFontFamily}; font-size: ${hs.fontSize}; color: ${hs.color || FALLBACK_HEADING_COLOR}; font-weight: ${hs.fontWeight}; margin: 0 0 10px;`;
  return `
    a { color: ${gs.linkColor || FALLBACK_LINK_COLOR}; text-decoration: ${linkDecoration(gs)}; }
    h1 { ${h(gs.h1)} }
    h2 { ${h(gs.h2)} }
    h3 { ${h(gs.h3)} }
    p { margin: 0 0 10px; }
    ul { list-style-type: disc; margin: 0 0 10px 20px; padding: 0; }
    ol { list-style-type: decimal; margin: 0 0 10px 20px; padding: 0; }
    li { margin: 0 0 4px; }
  `;
}

/** Body/text color guaranteed non-empty (used by the compiler for body + text blocks). */
export function resolveTextColor(...candidates: string[]): string {
  return candidates.find((c) => c && c.trim()) || FALLBACK_TEXT_COLOR;
}

/** Set each property only when the element does not already declare it inline (existing wins). */
function applyIfAbsent(el: HTMLElement, styles: Record<string, string>): void {
  for (const [prop, val] of Object.entries(styles)) {
    if (!val) continue;
    if (!el.style.getPropertyValue(prop)) el.style.setProperty(prop, val);
  }
}

/**
 * Inline the global heading / paragraph / link styles onto the matching tags
 * inside a rich-text content fragment. Existing inline styles are preserved
 * (a color the user set on selected text always wins). Returns the fragment
 * HTML unchanged if DOMParser is unavailable (e.g. SSR).
 */
export function inlineContentStyles(html: string, gs: GlobalStyles): string {
  if (!html || typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const { body } = doc;
  if (!body) return html;

  const headings: Array<[string, HeadingStyle]> = [
    ['h1', gs.h1],
    ['h2', gs.h2],
    ['h3', gs.h3],
  ];
  for (const [tag, hs] of headings) {
    body.querySelectorAll(tag).forEach((node) => applyIfAbsent(node as HTMLElement, headingStyleMap(gs, hs)));
  }
  body.querySelectorAll('p').forEach((node) => applyIfAbsent(node as HTMLElement, { margin: '0 0 10px' }));
  body.querySelectorAll('a').forEach((node) => applyIfAbsent(node as HTMLElement, linkStyleMap(gs)));
  body.querySelectorAll('ul').forEach((node) => applyIfAbsent(node as HTMLElement, listStyleMap('disc')));
  body.querySelectorAll('ol').forEach((node) => applyIfAbsent(node as HTMLElement, listStyleMap('decimal')));
  body.querySelectorAll('li').forEach((node) => applyIfAbsent(node as HTMLElement, LIST_ITEM_STYLE));

  return body.innerHTML;
}
